import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const processed = useRef(false);
  const [status, setStatus] = useState('Authenticating...');

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    handle();
  }, []);

  const handle = async () => {
    try {
      // Check for error from provider
      const error = searchParams.get('error');
      if (error) {
        navigate('/auth', { state: { authError: true }, replace: true });
        return;
      }

      // PKCE: exchange code for session
      const code = searchParams.get('code');
      if (code) {
        setStatus('Completing sign-in...');
        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError) {
          console.error('Code exchange failed:', exchangeError);
          navigate('/auth', { state: { authError: true }, replace: true });
          return;
        }
        if (data.session) {
          await syncAndRedirect(data.session);
          return;
        }
      }

      // Implicit flow: tokens in hash fragment
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        setStatus('Completing sign-in...');
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const params = new URLSearchParams(hash.substring(1));
          if (params.get('type') === 'recovery') {
            navigate('/auth/reset-password');
            return;
          }
          await syncAndRedirect(session);
          return;
        }
      }

      // Fallback: wait for Supabase to settle
      await new Promise((r) => setTimeout(r, 1000));
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await syncAndRedirect(session);
        return;
      }

      navigate('/auth', { state: { authError: true }, replace: true });
    } catch (err) {
      console.error('Auth callback error:', err);
      navigate('/auth', { state: { authError: true }, replace: true });
    }
  };

  const syncAndRedirect = async (session) => {
    setStatus('Setting up your account...');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/auth/supabase-session`,
        { access_token: session.access_token },
        { withCredentials: true },
      );

      // ── New user without role: onboarding required ──
      if (res.data.needs_onboarding) {
        navigate('/auth?mode=google-onboarding', {
          state: {
            onboarding: true,
            email: res.data.email,
            name: res.data.name,
            picture: res.data.picture,
            preset_role: res.data.preset_role || null,
          },
          replace: true,
        });
        return;
      }

      // ── Existing or completed user: go to dashboard ──
      const user = res.data.user;
      toast.success('Welcome!');
      if (user.role === 'regional_admin') navigate('/regional-admin', { state: { user }, replace: true });
      else if (user.role === 'employer') navigate('/employer/dashboard', { state: { user }, replace: true });
      else navigate('/dashboard', { state: { user }, replace: true });
    } catch (err) {
      console.error('Backend sync failed:', err);
      toast.error('Failed to complete sign-in. Please try again.');
      navigate('/auth');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f7f7f7]" data-testid="auth-callback-page">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
        <p className="text-[#666]">{status}</p>
      </div>
    </div>
  );
}

export default AuthCallback;
